import { useState, useEffect, useCallback } from "react";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { fetchListingDetails } from "utils/listing/fetchListingDetails";
import { dataExists } from "utils/validations";
import { logError } from "utils/error";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../../constants/ZoomAttendance.constants";

export const useSessionData = ({ listingUuid, slotUuid }) => {
  const [sessionDetails, setSessionDetails] = useState(null);
  const [dataOfZoomAttendanceOfListing, setDataOfZoomAttendanceOfListing] =
    useState({});
  const [fullListingData, setFullListingData] = useState(null);

  const { data: meetingData } = useCustomQuery({
    url: ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS,
    params: {
      listing_uuid: listingUuid,
      page: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_ALL,
    },
    queryKey: [
      ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS,
      listingUuid,
      ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_ALL,
    ],
    staleTime: ZOOM_ATTENDANCE_CONSTANTS.QUERY_CACHE_TIME_MS,
    enabled: Boolean(listingUuid && slotUuid),
    onError: (err) => {
      logError({
        error: err,
        when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES
          .FETCH_SESSION_DETAILS_ERROR_WHEN,
        occuredAt: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.PAGE_OCCURRENCE,
      });
    },
  });

  useEffect(() => {
    const meetingDetails =
      meetingData?.meeting_details || meetingData?.data?.meeting_details;
    if (meetingDetails && slotUuid) {
      const matched = meetingDetails.find(
        (s) => String(s.slot_uuid) === String(slotUuid)
      );
      if (matched) {
        setSessionDetails(matched);
      }
    }
  }, [meetingData, slotUuid]);

  const fetchListingInfo = useCallback(async () => {
    if (!listingUuid) return;
    try {
      const apiResponse = await fetchListingDetails({ listingUuid });
      const listingData = apiResponse?.data?.event || {};
      setFullListingData(listingData);
      const listingName = listingData.title;
      const listingType = listingData.type;
      const metadata = listingData.metadata || {};
      const coverImage = listingData.cover_image || listingData.image_url || "";
      const scheduleTypeLabel =
        listingData.type_title ||
        listingData.schedule_type_label ||
        ZOOM_ATTENDANCE_CONSTANTS.LABELS.FIXED_CLASS_DEFAULT_LABEL;
      const newState = {
        listingUuid,
        listingName,
        listingType,
        metadata,
        coverImage,
        scheduleTypeLabel,
      };

      if (!listingName || !dataExists(listingType)) {
        throw new Error(
          ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.INVALID_DATA +
            JSON.stringify(newState)
        );
      }
      setDataOfZoomAttendanceOfListing(newState);
    } catch (err) {
      logError({
        error: err,
        when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES
          .FETCH_LISTING_DETAILS_ERROR_WHEN,
        occuredAt: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.PAGE_OCCURRENCE,
      });
    }
  }, [listingUuid]);

  useEffect(() => {
    fetchListingInfo();
  }, [fetchListingInfo]);

  return {
    sessionDetails,
    dataOfZoomAttendanceOfListing,
    fullListingData,
  };
};
